import type { DebugStateHandler } from '../../../libs/offerSystem/offerSystemTypes';
import { MS_IN_DAY, MS_IN_HOUR, MS_IN_MINUTE } from '../../../utils/time-utils';
import { InventoryItem } from '../../resources/types';
import type { DebugOfferState } from './offerDebugState';

export class DebugOfferStateHandler implements DebugStateHandler<DebugOfferState> {
  public getInfo(state: DebugOfferState): string {
    const inventory = Object.entries(state.getInventory())
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n');

    return `Level: ${state.getLevelNumber()}
    Band: ${state.getLastBand()}
    Current time in hours: ${new Date(state.getCurrentTime()).toUTCString()},
    User create time: ${new Date(state.getUserCreateDate()).toUTCString()},
    ${inventory}
    `;
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  public actions: Record<string, Record<string, (state: DebugOfferState) => void>> = {
    User: {
      'Increase level': (state) => {
        state.setLevelNumber(state.getLevelNumber() + 1);
      },
    },
    'Spending Band': ((): Record<string, (state: DebugOfferState) => void> => {
      const output: Record<string, (state: DebugOfferState) => void> = {};

      [1, 2, 3, 4, 5, 6].forEach((bandValue) => {
        output[`Set band to ${bandValue}`] = (state): void => {
          state.setBand(bandValue);
        };
      });

      return output;
    })(),
    'Creation Time': {
      'User creation time -1 day': (state): void => {
        state.setUserCreateDate(state.getUserCreateDate() - MS_IN_DAY);
      },
      'User creation time -5 day': (state): void => {
        state.setUserCreateDate(state.getUserCreateDate() - 5 * MS_IN_DAY);
      },
    },
    'Current Time': {
      'Increase time by 5 minutes': (state) => {
        state.setCurrentTime(state.getCurrentTime() + MS_IN_MINUTE * 5);
      },
      'Increase time by 1 hour': (state) => {
        state.setCurrentTime(state.getCurrentTime() + MS_IN_HOUR);
      },
      'Increase time by 6 hours': (state) => {
        state.setCurrentTime(state.getCurrentTime() + MS_IN_HOUR * 6);
      },
      'Increase time by 1 day': (state) => {
        state.setCurrentTime(state.getCurrentTime() + MS_IN_DAY);
      },
    },
    Inventory: ((): Record<string, (state: DebugOfferState) => void> => {
      const output: Record<string, (state: DebugOfferState) => void> = {};

      [
        { item: InventoryItem.Coin, amounts: [2000, 500, 50, -50, -500, -2000] },
        { item: InventoryItem.BoosterHammer, amounts: [5, 1, -1, -5] },
        { item: InventoryItem.BoosterRedraw, amounts: [5, 1, -1, -5] },
        { item: InventoryItem.BoosterMove, amounts: [5, 1, -1, -5] },
      ].forEach(({ item, amounts }) => {
        amounts.forEach((amount) => {
          output[`${item} ${amount > 0 ? '+' : ''}${amount}`] = (state): void => {
            const inventory = state.getInventory();
            inventory[item] = Math.max((inventory[item] ?? 0) + amount, 0);
          };
        });
        output[`Set ${item} to 0`] = (state): void => {
          state.getInventory()[item] = 0;
        };
      });

      return output;
    })(),
  };
  /* eslint-enable @typescript-eslint/naming-convention */
}
